// Cloudflare Turnstile — 빌드 때 박힌 사이트 키가 있을 때만 스크립트를 한 번 싣고 위젯을 그린다(키가 없으면 아무것도 안 싣는다).

// 서버 TURNSTILE_SIGNUP_ACTION 과 같은 값 — core lib/turnstile 은 secret 을 끌어와 브라우저에서 import 하지 않는다(테스트가 맞춰 본다).
export const TURNSTILE_SIGNUP_ACTION = "signup";

export const TURNSTILE_SCRIPT_SRC = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

export type TurnstileSize = "normal" | "compact";

// 기본 위젯은 300px 고정 — 자리가 그보다 좁으면(375px 폰 카드 안쪽 ~293px) compact 로 그려 넘치지 않게 한다.
export const TURNSTILE_NORMAL_WIDTH = 300;
export const turnstileSizeOf = (width: number | undefined): TurnstileSize => (typeof width === "number" && width < TURNSTILE_NORMAL_WIDTH ? "compact" : "normal");

interface TurnstileRenderOptions {
  sitekey: string;
  action: string;
  size: TurnstileSize;
  callback: (token: string) => void;
  "expired-callback": () => void;
  "error-callback": () => void;
}

export interface TurnstileApi {
  render: (container: HTMLElement, options: TurnstileRenderOptions) => string | undefined;
  remove: (widgetId: string) => void;
}

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

// 키가 비었거나 op:// 그대로면(op run 없이 빌드) 끈다 — 서버도 비밀이 없으면 건너뛴다.
export const turnstileSiteKeyOf = (env: Pick<ImportMetaEnv, "VITE_TURNSTILE_SITE_KEY">): string | null => {
  const key = env.VITE_TURNSTILE_SITE_KEY?.trim();
  return key && !key.startsWith("op://") ? key : null;
};

interface LoaderDeps {
  win: { turnstile?: TurnstileApi };
  doc: Pick<Document, "createElement" | "head">;
}

// 실패하면 붙인 태그를 떼고 약속을 비워 다음 마운트에서 다시 받는다(daum-postcode 와 같은 모양).
export const createTurnstileLoader = (deps: () => LoaderDeps) => {
  let loading: Promise<TurnstileApi> | null = null;
  return (): Promise<TurnstileApi> => {
    const { win, doc } = deps();
    if (win.turnstile) return Promise.resolve(win.turnstile);
    if (loading) return loading;
    const script = doc.createElement("script");
    loading = new Promise<TurnstileApi>((resolve, reject) => {
      script.src = TURNSTILE_SCRIPT_SRC;
      script.async = true;
      script.onload = () => (win.turnstile ? resolve(win.turnstile) : reject(new Error("자동 가입 방지 확인을 불러오지 못했습니다")));
      script.onerror = () => reject(new Error("자동 가입 방지 확인을 불러오지 못했습니다"));
      doc.head.appendChild(script);
    }).catch((error: unknown) => {
      script.remove();
      loading = null;
      throw error;
    });
    return loading;
  };
};

export const loadTurnstile = createTurnstileLoader(() => ({ win: window, doc: document }));

// load = 스크립트를 못 받았거나 render 가 실패 · challenge = 위젯의 error-callback(챌린지 실패·네트워크 등).
export type TurnstileFailure = "load" | "challenge";

export interface TurnstileHandlers {
  onToken: (token: string | null) => void;
  // 화면에 안내(다시 시도)를 띄운다.
  onFail: (failure: TurnstileFailure) => void;
}

// 가입 위젯을 그리고 토큰을 넘긴다(만료·오류면 null · 오류는 onFail 도) · 돌려준 함수가 위젯을 떼고 늦게 온 로드도 무시한다.
export const mountTurnstile = (
  container: HTMLElement,
  siteKey: string,
  { onToken, onFail }: TurnstileHandlers,
  load: () => Promise<TurnstileApi> = loadTurnstile,
): (() => void) => {
  let disposed = false;
  let remove: (() => void) | null = null;
  load()
    .then((api) => {
      if (disposed) return;
      const id = api.render(container, {
        sitekey: siteKey,
        action: TURNSTILE_SIGNUP_ACTION,
        size: turnstileSizeOf(container.offsetWidth),
        callback: (token) => onToken(token),
        "expired-callback": () => onToken(null),
        "error-callback": () => {
          if (disposed) return;
          onToken(null);
          onFail("challenge");
        },
      });
      if (!id) throw new Error("Turnstile 위젯을 그리지 못했습니다");
      remove = () => api.remove(id);
    })
    .catch((error: unknown) => {
      console.error("[buildpy] Turnstile 을 불러오지 못했습니다", error);
      if (disposed) return;
      onToken(null);
      onFail("load");
    });
  return () => {
    disposed = true;
    remove?.();
    onToken(null);
  };
};
